import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUpCircle,
  ExternalLink,
  Pause,
  Play,
  RefreshCcw,
  HardDriveDownload,
  Copy,
  Activity,
  Trash2,
  Link2,
  AlertCircle,
  CheckCircle2,
  Globe,
  Plus,
  Pencil,
  Server,
  Loader2,
  GitBranch,
} from 'lucide-react';
import { PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatusDot } from '@/components/ui/StatusDot';
import { PropertyChips } from '@/components/ui/Tabs';
import { Modal } from '@/components/ui/Modal';
import {
  useTenant as useApiTenant,
  useWorkspace as useApiWorkspaceDetail,
  useRestartWorkspace,
  useSnapshotWorkspace,
  useSuspendWorkspace,
  useResumeWorkspace,
  useResizeWorkspace,
  useAddCustomDomain,
  useRemoveCustomDomain,
  useSyncDns,
  usePatchWorkspace,
  useDestroyWorkspace,
  useCloneWorkspace,
  useWorkspaceSnapshots,
  useUpgradeWorkspace,
  useUpgradeStatus,
} from '@/api/tenants';
import { ProvisioningProgress } from '@/components/provisioning/ProvisioningProgress';
import { useAuditEntries } from '@/api/audit';
import { useDroplet, useDroplets, useLinkDroplet } from '@/api/droplets';
import { workspaceSummaryToWorkspace } from '@/api/adapters';
import { toast } from '@/components/ui/Toaster';
import { DROPLET_SIZE_LABEL, DROPLET_SIZE_COST, type DropletSize } from '@/lib/droplet';
import { REGION_LABEL } from '@/lib/tier';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/cn';

export function WorkspaceDetailRoute() {
  const { slug, workspaceId } = useParams();
  const { data: apiTenant, isLoading } = useApiTenant(slug);
  const apiWorkspace = apiTenant?.workspaces.find(w => w.id === workspaceId);

  const tenant = apiTenant
    ? { id: apiTenant.id, slug: apiTenant.slug, name: apiTenant.name, tier: apiTenant.tier }
    : undefined;
  const workspace = apiWorkspace && tenant
    ? workspaceSummaryToWorkspace(apiWorkspace, tenant.id, tenant.tier)
    : undefined;

  const restartMutation = useRestartWorkspace();
  const snapshotMutation = useSnapshotWorkspace();
  const suspendMutation = useSuspendWorkspace();
  const resumeMutation = useResumeWorkspace();
  const { data: apiAudit } = useAuditEntries();
  const { data: droplet } = useDroplet(apiWorkspace?.droplet_id || undefined);
  const { data: snapshots } = useWorkspaceSnapshots(slug, workspaceId);
  // Full detail (incl. live SSL probe) — summary from the tenant query
  // doesn't carry certificate info.
  const { data: wsDetail } = useApiWorkspaceDetail(slug, workspaceId);

  const upgradeMutation = useUpgradeWorkspace();
  // Poll upgrade status whenever a version drift exists or we just
  // kicked one off; toast exactly once on the running->done transition.
  const [watchUpgrade, setWatchUpgrade] = useState(false);
  const versionDrift = Boolean(
    apiWorkspace?.version && apiWorkspace?.latest_version &&
    apiWorkspace.version !== 'unknown' && apiWorkspace.version !== apiWorkspace.latest_version,
  );
  const { data: upgradeState } = useUpgradeStatus(slug, workspaceId, versionDrift || watchUpgrade);
  const upgrading = upgradeState?.status === 'queued' || upgradeState?.status === 'running';
  const prevUpgradeStatus = useRef<string | undefined>(undefined);
  useEffect(() => {
    const st = upgradeState?.status;
    const prev = prevUpgradeStatus.current;
    if ((prev === 'queued' || prev === 'running') && st && st !== prev) {
      if (st === 'success') toast.success('Upgrade finished', 'The instance is back on the latest version.');
      if (st === 'failed') toast.danger('Upgrade failed', (upgradeState?.log_tail ?? '').slice(-300) || 'Check the ops-runner log on the admin server.');
    }
    prevUpgradeStatus.current = st;
  }, [upgradeState?.status, upgradeState?.log_tail]);

  async function doUpgrade() {
    if (!workspace || !tenant) return;
    const reason = window.prompt(
      `Upgrade ${workspace.name} from ${workspace.version} to ${workspace.latestVersion}?\n` +
      "Runs the app's own updater (migrations + rebuild + service reload), usually 2-5 minutes " +
      'with a brief blip while services restart. Why?',
      'Routine version upgrade',
    );
    if (!reason) return;
    try {
      const r = await upgradeMutation.mutateAsync({ tenantId: tenant.slug, workspaceId: workspace.id, reason });
      setWatchUpgrade(true);
      toast.info('Upgrade queued', r.message);
    } catch (e: any) {
      toast.danger('Could not queue upgrade', e?.message ?? 'Unknown error');
    }
  }

  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [domainModalOpen, setDomainModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [resizeModalOpen, setResizeModalOpen] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [destroyModalOpen, setDestroyModalOpen] = useState(false);
  const removeDomain = useRemoveCustomDomain();
  const syncDns = useSyncDns();

  async function doRestart() {
    if (!workspace || !tenant) return;
    const reason = window.prompt(`Restart ${workspace.name}? Why?`, 'Routine restart');
    if (!reason) return;
    try {
      const r = await restartMutation.mutateAsync({ tenantId: tenant.slug, workspaceId: workspace.id, reason });
      toast.success(`${workspace.name} restart queued`, r.message);
    } catch (e: any) {
      toast.danger('Restart failed', e?.message ?? 'Unknown error');
    }
  }

  async function doSnapshot() {
    if (!workspace || !tenant) return;
    try {
      const r = await snapshotMutation.mutateAsync({ tenantId: tenant.slug, workspaceId: workspace.id });
      toast.success(
        `${workspace.name} snapshot queued on DigitalOcean`,
        `${r.message} Snapshots take 2–5 minutes; check the DO dashboard for progress.`,
      );
    } catch (e: any) {
      toast.danger('Snapshot failed', e?.message ?? 'Unknown error');
    }
  }

  async function doSuspend() {
    if (!workspace || !tenant) return;
    const reason = window.prompt(`Take ${workspace.name} offline? Visitors will see an error page until you press Resume. Why?`, '');
    if (!reason) return;
    try {
      const r = await suspendMutation.mutateAsync({ tenantId: tenant.slug, workspaceId: workspace.id, reason });
      toast.warn(`${workspace.name} suspended`, r.message);
    } catch (e: any) {
      toast.danger('Suspend failed', e?.message ?? 'Unknown error');
    }
  }

  async function doResume() {
    if (!workspace || !tenant) return;
    try {
      const r = await resumeMutation.mutateAsync({ tenantId: tenant.slug, workspaceId: workspace.id });
      toast.success(`${workspace.name} resumed`, r.message);
    } catch (e: any) {
      toast.danger('Resume failed', e?.message ?? 'Unknown error');
    }
  }

  async function doSyncDns() {
    if (!workspace || !tenant) return;
    try {
      const r = await syncDns.mutateAsync({ tenantId: tenant.slug, workspaceId: workspace.id });
      toast.success('DNS synced', r.message);
    } catch (e: any) {
      toast.danger('DNS sync failed', e?.message ?? 'Unknown error');
    }
  }

  if (!tenant || !workspace) {
    return (
      <PageBody>
        <Card className="p-8 text-center">
          <div className="text-md font-semibold">
            {isLoading ? 'Loading…' : 'Instance not found'}
          </div>
          <Link to="/tenants" className="text-accent text-sm mt-2 inline-block">← Back to customers</Link>
        </Card>
      </PageBody>
    );
  }

  const needsUpgrade = workspace.version && workspace.latestVersion && workspace.version !== workspace.latestVersion;
  const suspended = workspace.status === 'suspended';
  const isLinked = Boolean(apiWorkspace?.droplet_id);

  const ssl = wsDetail?.ssl;
  const sslChip = ssl
    ? `${ssl.wildcard ? 'Wildcard' : 'Per-host'}${ssl.expires ? ` · exp ${new Date(ssl.expires).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}`
    : workspace.status === 'provisioning' ? 'Pending' : 'Unreachable';
  const hb = apiWorkspace?.last_heartbeat_at;
  const hbAgeSec = hb ? Math.round((Date.now() - new Date(hb).getTime()) / 1000) : null;
  const hbChip = hbAgeSec == null
    ? 'None yet'
    : hbAgeSec < 150 ? `${relativeTime(hb!)} ✓` : `${relativeTime(hb!)} — stale`;
  const workspaceEvents = (apiAudit ?? [])
    .filter(e => e.workspace_id === workspace.id || e.target.includes(workspace.id))
    .slice(0, 12);

  return (
    <>
      <div className="px-4 md:px-6 lg:px-8 pt-5 pb-4 bg-bg-elevated border-b border-border">
        <Link
          to={`/tenants/${tenant.slug}/workspaces`}
          className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg mb-3"
        >
          <ArrowLeft size={14} /> {tenant.name} instances
        </Link>

        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <StatusDot status={workspace.status} />
              <h1 className="text-xl md:text-2xl font-semibold tracking-tight">{workspace.name}</h1>
              <Badge tone="neutral">{apiWorkspace?.environment ?? 'workspace'}</Badge>
              {needsUpgrade && <Badge tone="warn">{workspace.latestVersion} available</Badge>}
              <button
                className="text-fg-subtle hover:text-fg ml-1 inline-flex items-center gap-1 text-2xs font-mono"
                onClick={() => navigator.clipboard?.writeText(workspace.id)}
                title="Copy instance ID"
              >
                <Copy size={11} /> {workspace.id}
              </button>
            </div>
          </div>
          <a href={workspace.url} target="_blank" rel="noreferrer">
            <Button variant="primary" size="sm" iconRight={<ExternalLink size={13} />}>
              Open app
            </Button>
          </a>
        </div>

        <PropertyChips
          chips={[
            { label: 'Customer', value: <Link to={`/tenants/${tenant.slug}`} className="text-accent hover:underline">{tenant.name}</Link> },
            { label: 'Hostname', value: (apiWorkspace?.default_hostname ?? workspace.url.replace(/^https?:\/\//, '')), mono: true },
            { label: 'Region', value: REGION_LABEL[workspace.region] },
            { label: 'Droplet', value: DROPLET_SIZE_LABEL[apiWorkspace?.droplet_size ?? 's-2vcpu-4gb'].replace(/ —.*/, '') },
            { label: 'Created', value: new Date(workspace.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) },
            { label: 'SSL', value: sslChip },
            { label: 'Heartbeat', value: hbChip },
          ]}
        />

        {/* Action bar — visible on landing, no hunting in sidebars */}
        <div className="mt-4 pt-3 border-t border-border flex items-center gap-2 flex-wrap">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold mr-2">Actions</div>

          {versionDrift && (
            <Button
              variant="primary" size="sm"
              iconLeft={upgrading ? <Loader2 size={13} className="animate-spin" /> : <ArrowUpCircle size={13} />}
              onClick={doUpgrade}
              disabled={upgrading || !isLinked}
              title={`Update NousViz from ${workspace.version} to ${workspace.latestVersion} — runs migrations and rebuilds, ~2-5 min`}
            >
              {upgrading
                ? (upgradeState?.status === 'queued' ? 'Upgrade queued…' : 'Upgrading…')
                : `Upgrade to ${workspace.latestVersion}`}
            </Button>
          )}

          {/* Runtime actions: act on the live droplet */}
          <Button variant="secondary" size="sm" iconLeft={<HardDriveDownload size={13} />} onClick={doSnapshot} disabled={!isLinked} title="Save a full backup image of this server (takes 2-5 min)">
            Snapshot
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<RefreshCcw size={13} />} onClick={doRestart} disabled={!isLinked} title="Reboot the server — the site is briefly unavailable">
            Restart
          </Button>
          {suspended ? (
            <Button variant="secondary" size="sm" iconLeft={<Play size={13} />} onClick={doResume} disabled={!isLinked} title="Bring the instance back online">
              Resume
            </Button>
          ) : (
            <Button variant="secondary" size="sm" iconLeft={<Pause size={13} />} onClick={doSuspend} disabled={!isLinked} title="Take the instance offline — visitors see an error page until you Resume">
              Suspend
            </Button>
          )}

          <span className="mx-1 h-5 w-px bg-border" />

          {/* Config actions: change workspace identity / size */}
          <Button variant="secondary" size="sm" iconLeft={<Pencil size={13} />} onClick={() => setRenameModalOpen(true)}>
            Rename
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<Server size={13} />} onClick={() => setResizeModalOpen(true)} disabled={!isLinked} title="Move the instance to a bigger or smaller server">
            Resize
          </Button>

          <span className="mx-1 h-5 w-px bg-border" />

          {/* Lifecycle: spawn / destroy */}
          <Button variant="secondary" size="sm" iconLeft={<GitBranch size={13} />} onClick={() => setCloneModalOpen(true)} disabled={!isLinked} title="Copy this instance: backs it up and creates a new instance from the backup">
            Clone to new instance
          </Button>
          <Button variant="danger" size="sm" iconLeft={<Trash2 size={13} />} onClick={() => setDestroyModalOpen(true)}>
            Destroy
          </Button>
        </div>
      </div>

      <PageBody>
        <div className="mb-4 empty:mb-0">
          <ProvisioningProgress
            tenantId={tenant.slug}
            workspaceId={workspace.id}
            status={workspace.status}
          />
        </div>

        {!isLinked && (
          <Card className="mb-4 border-[color:var(--warn)]/40 bg-[color:var(--warn-soft)]/50">
            <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
              <AlertCircle size={16} className="text-[color:var(--warn)] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-fg">
                  Not linked to its server
                </div>
                <div className="text-xs text-fg-muted">
                  Restart, snapshot, resize, and suspend won't work until you link this instance to its DigitalOcean server (droplet).
                </div>
              </div>
              <Button variant="primary" size="sm" iconLeft={<Link2 size={13} />} onClick={() => setLinkModalOpen(true)}>
                Link droplet
              </Button>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">
            <Card>
              <CardHeader
                title="Recent activity"
                description="Everything operators have done to this instance, newest first."
              />
              <CardBody className="space-y-2 text-sm">
                {workspaceEvents.length === 0 ? (
                  <div className="text-fg-subtle text-center py-6">
                    No activity yet for this instance.
                  </div>
                ) : (
                  workspaceEvents.map(e => (
                    <div key={e.id} className="flex items-baseline justify-between gap-3 py-1 border-b border-border/60 last:border-0">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <span className="font-mono text-xs text-fg-subtle tabnum w-24 shrink-0">
                          {relativeTime(e.timestamp)}
                        </span>
                        <span className="font-mono text-xs text-fg">{e.action}</span>
                        <span className="text-fg-muted truncate">· {e.target}</span>
                      </div>
                      <Badge tone={e.result === 'success' ? 'success' : 'danger'}>{e.result}</Badge>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader title="Live tail" description="Stream this workspace's logs as they happen." />
              <CardBody>
                <Link to={`/tenants/${tenant.slug}/logs?workspace=${workspace.id}`}>
                  <Button variant="secondary" size="sm" className="w-full justify-start" iconLeft={<Activity size={14} />}>
                    Open log viewer
                  </Button>
                </Link>
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title={<><Globe size={13} className="inline -mt-0.5 mr-1.5 text-fg-muted" /> Hostnames</>}
                description="Where customers can reach this workspace."
                actions={
                  <div className="flex items-center gap-1">
                    {isLinked && apiWorkspace?.default_hostname && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconLeft={<RefreshCcw size={12} />}
                        onClick={doSyncDns}
                        disabled={syncDns.isPending}
                        title="Upsert Cloudflare A record for the default hostname → droplet IP"
                      >
                        {syncDns.isPending ? 'Repairing…' : 'Repair DNS'}
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconLeft={<Plus size={12} />}
                      onClick={() => setDomainModalOpen(true)}
                    >
                      Add domain
                    </Button>
                  </div>
                }
              />
              <CardBody className="space-y-2.5 text-sm">
                {apiWorkspace?.default_hostname ? (
                  <div className="flex items-center justify-between gap-3 pb-2 border-b border-border/60">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-fg truncate">{apiWorkspace.default_hostname}</div>
                      <div className="text-2xs text-fg-subtle">Default hostname</div>
                    </div>
                    <Badge tone="accent">Primary</Badge>
                  </div>
                ) : (
                  <div className="text-2xs text-fg-subtle italic">
                    No default hostname set in tenants.yaml.
                  </div>
                )}

                {(apiWorkspace?.custom_domains ?? []).length === 0 ? (
                  <div className="text-2xs text-fg-subtle pt-1">
                    No custom domains. Customers can point their own domain at this workspace via CNAME.
                  </div>
                ) : (
                  (apiWorkspace?.custom_domains ?? []).map(d => (
                    <div key={d} className="flex items-center justify-between gap-3 py-1.5 border-b border-border/60 last:border-0">
                      <div className="min-w-0">
                        <div className="font-mono text-xs text-fg truncate">{d}</div>
                        <div className="text-2xs text-fg-subtle">Custom domain</div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <a
                          href={`https://${d}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-fg-subtle hover:text-accent"
                          title="Open in new tab"
                        >
                          <ExternalLink size={12} />
                        </a>
                        <button
                          onClick={async () => {
                            if (!confirm(`Remove ${d}?`)) return;
                            try {
                              await removeDomain.mutateAsync({
                                tenantId: tenant.slug,
                                workspaceId: workspace.id,
                                domain: d,
                              });
                              toast.info(`Removed ${d}`, 'Update DNS to stop pointing at this workspace.');
                            } catch (e: any) {
                              toast.danger('Remove failed', e?.message ?? 'Unknown error');
                            }
                          }}
                          className="text-fg-subtle hover:text-[color:var(--danger)]"
                          title="Remove custom domain"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader
                title="DigitalOcean droplet"
                actions={
                  isLinked && (
                    <Badge tone="success" icon={<CheckCircle2 size={9} />}>Linked</Badge>
                  )
                }
              />
              <CardBody className="space-y-2 text-sm">
                {isLinked ? (
                  <>
                    <Row label="Droplet" value={droplet?.name ?? apiWorkspace?.droplet_id} mono />
                    <Row label="Droplet ID" value={apiWorkspace?.droplet_id ?? '—'} mono />
                    {droplet?.status && (
                      <Row
                        label="Status"
                        value={<span className="capitalize text-fg">{droplet.status}</span>}
                      />
                    )}
                    {droplet?.ipv4 && <Row label="Public IPv4" value={droplet.ipv4} mono />}
                    <Row label="Region" value={droplet?.region ? REGION_LABEL[droplet.region] ?? droplet.region : REGION_LABEL[workspace.region]} />
                    <Row label="Size" value={droplet?.size ?? apiWorkspace?.droplet_size ?? '—'} mono />
                    {droplet?.created_at && (
                      <Row label="Created" value={new Date(droplet.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} />
                    )}
                  </>
                ) : (
                  <div className="text-fg-muted text-sm py-2">
                    No droplet linked.
                    <button
                      onClick={() => setLinkModalOpen(true)}
                      className="text-accent hover:underline ml-1"
                    >
                      Link one →
                    </button>
                  </div>
                )}
              </CardBody>
            </Card>

            {isLinked && (
              <Card>
                <CardHeader
                  title={<><HardDriveDownload size={13} className="inline -mt-0.5 mr-1.5 text-fg-muted" /> Snapshots</>}
                  description={
                    snapshots && snapshots.in_progress.length > 0
                      ? `${snapshots.in_progress.length} in progress · ${snapshots.completed.length} stored`
                      : snapshots && snapshots.completed.length > 0
                        ? `${snapshots.completed.length} stored in DigitalOcean`
                        : 'Take a snapshot above to back up the droplet.'
                  }
                />
                <CardBody className="space-y-2 text-sm">
                  {snapshots?.in_progress.map(s => (
                    <div key={s.action_id} className="flex items-center gap-2.5 py-1.5 border-b border-border/60 last:border-0">
                      <Loader2 size={13} className="animate-spin text-accent shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-fg">Snapshotting now…</div>
                        <div className="text-2xs text-fg-subtle">
                          Started {s.started_at ? relativeTime(s.started_at) : 'just now'} · usually 2–5 min
                        </div>
                      </div>
                    </div>
                  ))}
                  {snapshots && snapshots.completed.length === 0 && snapshots.in_progress.length === 0 && (
                    <div className="text-fg-subtle text-2xs py-2">
                      No snapshots yet. Click Snapshot above to take one — useful before deploys, schema changes, or as a base for Clone.
                    </div>
                  )}
                  {snapshots?.completed.slice(0, 5).map(s => (
                    <div key={s.id} className="flex items-start justify-between gap-3 py-1.5 border-b border-border/60 last:border-0">
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-xs text-fg truncate">{s.name}</div>
                        <div className="text-2xs text-fg-subtle">
                          {s.created_at ? relativeTime(s.created_at) : '—'}
                          {s.regions.length > 0 && ` · ${s.regions[0]}`}
                        </div>
                      </div>
                      <div className="text-2xs text-fg-muted tabnum shrink-0">
                        {s.size_gigabytes.toFixed(1)} GB
                      </div>
                    </div>
                  ))}
                  {snapshots && snapshots.completed.length > 5 && (
                    <div className="text-2xs text-fg-subtle pt-1">
                      Showing 5 of {snapshots.completed.length}. View all in the{' '}
                      <a
                        href="https://cloud.digitalocean.com/images/snapshots"
                        target="_blank"
                        rel="noreferrer"
                        className="text-accent hover:underline"
                      >
                        DigitalOcean dashboard
                      </a>.
                    </div>
                  )}
                </CardBody>
              </Card>
            )}
          </div>
        </div>
      </PageBody>

      <LinkDropletModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        tenantSlug={tenant.slug}
        workspaceId={workspace.id}
        workspaceName={workspace.name}
      />

      <AddCustomDomainModal
        open={domainModalOpen}
        onClose={() => setDomainModalOpen(false)}
        tenantSlug={tenant.slug}
        workspaceId={workspace.id}
        defaultHostname={apiWorkspace?.default_hostname ?? null}
      />

      <RenameWorkspaceModal
        open={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        tenantSlug={tenant.slug}
        workspaceId={workspace.id}
        currentName={workspace.name}
      />

      <ResizeWorkspaceModal
        open={resizeModalOpen}
        onClose={() => setResizeModalOpen(false)}
        tenantSlug={tenant.slug}
        workspaceId={workspace.id}
        workspaceName={workspace.name}
        currentSize={(apiWorkspace?.droplet_size ?? 's-2vcpu-4gb') as DropletSize}
      />

      <CloneWorkspaceModal
        open={cloneModalOpen}
        onClose={() => setCloneModalOpen(false)}
        tenantSlug={tenant.slug}
        workspaceId={workspace.id}
        workspaceName={workspace.name}
        sourceDropletSize={(apiWorkspace?.droplet_size ?? 's-2vcpu-4gb') as DropletSize}
      />

      <DestroyWorkspaceModal
        open={destroyModalOpen}
        onClose={() => setDestroyModalOpen(false)}
        tenantSlug={tenant.slug}
        workspaceId={workspace.id}
        workspaceName={workspace.name}
        hostname={apiWorkspace?.default_hostname ?? null}
        dropletId={apiWorkspace?.droplet_id ?? null}
        customDomains={apiWorkspace?.custom_domains ?? []}
      />
    </>
  );
}

function Row({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 last:border-0 pb-2 last:pb-0">
      <span className="text-fg-muted">{label}</span>
      <span className={mono ? 'font-mono text-xs text-fg' : 'text-fg font-medium'}>{value}</span>
    </div>
  );
}

function CloneWorkspaceModal({
  open,
  onClose,
  tenantSlug,
  workspaceId,
  workspaceName,
  sourceDropletSize,
}: {
  open: boolean;
  onClose: () => void;
  tenantSlug: string;
  workspaceId: string;
  workspaceName: string;
  sourceDropletSize: DropletSize;
}) {
  const nav = useNavigate();
  const clone = useCloneWorkspace();
  const [subdomain, setSubdomain] = useState('');
  const [subdomainTouched, setSubdomainTouched] = useState(false);
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState<'staging' | 'development' | 'production'>('staging');
  const [size, setSize] = useState<DropletSize>(sourceDropletSize);

  // Suggest a subdomain when env or name changes (until user touches it)
  function defaultSubdomain(env: string, n: string): string {
    const base = n.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '') || 'clone';
    return env === 'production' ? base : `${base}-${env.slice(0, 4)}`;
  }

  const validSubdomain = /^[a-z0-9][a-z0-9-]{1,40}[a-z0-9]$/.test(subdomain.trim());
  const validName = name.trim().length > 0;
  const canSubmit = validSubdomain && validName && !clone.isPending;
  const hostname = (() => {
    const s = subdomain.trim();
    if (!s) return '';
    if (environment === 'production') return `${s}.nousviz.app`;
    if (environment === 'staging') return `${s}.staging.nousviz.app`;
    return `${s}.dev.nousviz.app`;
  })();

  function reset() {
    setSubdomain(''); setSubdomainTouched(false); setName('');
    setEnvironment('staging'); setSize(sourceDropletSize);
  }

  async function submit() {
    if (!canSubmit) return;
    try {
      const r = await clone.mutateAsync({
        tenantId: tenantSlug,
        workspaceId,
        subdomain: subdomain.trim(),
        name: name.trim(),
        environment,
        droplet_size: size,
      });
      toast.info(`Cloning ${workspaceName}`, r.message, {
        to: `/tenants/${tenantSlug}/workspaces`,
        label: 'Watch progress',
      });
      reset();
      onClose();
      nav(`/tenants/${tenantSlug}/workspaces`);
    } catch (e: any) {
      toast.danger('Could not start clone', e?.message ?? 'Unknown error');
    }
  }

  const sizes: DropletSize[] = ['s-1vcpu-2gb', 's-2vcpu-4gb', 's-4vcpu-8gb', 's-8vcpu-16gb'];

  return (
    <Modal
      open={open}
      onClose={() => { if (!clone.isPending) { reset(); onClose(); } }}
      size="lg"
      title={`Clone ${workspaceName} → new workspace`}
      description={
        <>
          Snapshots the source droplet, boots a new one from the image,
          creates a Cloudflare A record for the new hostname, and registers
          the workspace. <strong>Manual finish step</strong>: after the
          new droplet is up, SSH in and run{' '}
          <code className="font-mono text-xs">scripts/post-clone-fixup.sh</code>{' '}
          to swap the nginx server_name + issue a fresh TLS cert.
          Total time ~5-10 min.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={clone.isPending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {clone.isPending ? <><Loader2 size={13} className="animate-spin mr-1.5" />Starting…</> : 'Clone workspace'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Workspace name <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={name}
            onChange={e => {
              const v = e.target.value;
              setName(v);
              if (!subdomainTouched) setSubdomain(defaultSubdomain(environment, v));
            }}
            placeholder="e.g. Staging Clone, QA Environment"
            autoFocus
            disabled={clone.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
              Environment
            </label>
            <select
              value={environment}
              onChange={e => {
                const env = e.target.value as any;
                setEnvironment(env);
                if (!subdomainTouched) setSubdomain(defaultSubdomain(env, name));
              }}
              disabled={clone.isPending}
              className="w-full h-8 px-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
            >
              <option value="staging">Staging</option>
              <option value="development">Development</option>
              <option value="production">Production</option>
            </select>
          </div>
          <div>
            <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
              Droplet size
            </label>
            <select
              value={size}
              onChange={e => setSize(e.target.value as DropletSize)}
              disabled={clone.isPending}
              className="w-full h-8 px-2 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
            >
              {sizes.map(s => (
                <option key={s} value={s}>
                  {DROPLET_SIZE_LABEL[s]} — ${DROPLET_SIZE_COST[s]}/mo
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Subdomain <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={subdomain}
            onChange={e => { setSubdomainTouched(true); setSubdomain(e.target.value.toLowerCase()); }}
            placeholder="e.g. statsdrone-test"
            disabled={clone.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono"
          />
          <div className="mt-1 text-2xs text-fg-subtle">
            Hostname: <code className="font-mono text-fg">{hostname || `<subdomain>.${environment === 'production' ? '' : environment === 'staging' ? 'staging.' : 'dev.'}nousviz.app`}</code>
          </div>
          {!validSubdomain && subdomain.length > 0 && (
            <div className="mt-1 text-2xs text-[color:var(--danger)] inline-flex items-center gap-1">
              <AlertCircle size={11} /> Lowercase letters, digits, dashes only
            </div>
          )}
        </div>

        <div className="text-2xs text-fg-subtle border-t border-border pt-3 leading-relaxed">
          On submit: <strong>DO snapshot</strong> of source droplet · <strong>new droplet</strong> created from image · Cloudflare A record →{' '}
          <code className="font-mono">{hostname || '<hostname>'}</code> · workspace registered.{' '}
          Then run{' '}
          <code className="font-mono">ssh root@&lt;new-ip&gt; 'bash -s' &lt; scripts/post-clone-fixup.sh --old-host=...&nbsp;--new-host={hostname || '...'}</code>{' '}
          to finish.
        </div>
      </div>
    </Modal>
  );
}

function DestroyWorkspaceModal({
  open,
  onClose,
  tenantSlug,
  workspaceId,
  workspaceName,
  hostname,
  dropletId,
  customDomains,
}: {
  open: boolean;
  onClose: () => void;
  tenantSlug: string;
  workspaceId: string;
  workspaceName: string;
  hostname: string | null;
  dropletId: string | null;
  customDomains: string[];
}) {
  const nav = useNavigate();
  const destroy = useDestroyWorkspace();
  const [confirm, setConfirm] = useState('');
  const [reason, setReason] = useState('');

  const canSubmit = confirm === workspaceId && reason.trim().length > 0 && !destroy.isPending;

  function reset() { setConfirm(''); setReason(''); }

  async function submit() {
    if (!canSubmit) return;
    try {
      const r = await destroy.mutateAsync({
        tenantId: tenantSlug,
        workspaceId,
        confirm,
        reason: reason.trim(),
      });
      toast.warn(`${workspaceName} destroyed`, r.message);
      reset();
      onClose();
      nav(`/tenants/${tenantSlug}/workspaces`);
    } catch (e: any) {
      toast.danger('Destroy failed', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!destroy.isPending) { reset(); onClose(); } }}
      size="md"
      title={
        <span className="inline-flex items-center gap-2 text-[color:var(--danger)]">
          <Trash2 size={16} /> Destroy {workspaceName}
        </span>
      }
      description={
        <div className="space-y-2">
          <p className="text-[color:var(--danger)] font-semibold">
            This is irreversible. Exactly this will be destroyed:
          </p>
          <ul className="text-xs space-y-1 list-disc pl-4">
            <li>
              DigitalOcean droplet{' '}
              {dropletId
                ? <code className="font-mono">{dropletId}</code>
                : <span className="text-fg-subtle">(none linked — registry entry only)</span>}
            </li>
            <li>
              Cloudflare A record{' '}
              {hostname
                ? <code className="font-mono">{hostname}</code>
                : <span className="text-fg-subtle">(no platform hostname)</span>}
            </li>
            <li>The instance's registry entry and ALL data on its server</li>
          </ul>
          {customDomains.length > 0 && (
            <p className="text-xs text-[color:var(--warn)]">
              ⚠ {customDomains.length} custom domain{customDomains.length > 1 ? 's' : ''} point
              {customDomains.length > 1 ? '' : 's'} at this instance
              (<code className="font-mono">{customDomains.join(', ')}</code>) and will go dark.
              Those DNS records live at the customer's provider and are not removed.
            </p>
          )}
        </div>
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => { reset(); onClose(); }} disabled={destroy.isPending}>Cancel</Button>
          <Button variant="danger" onClick={submit} disabled={!canSubmit}>
            {destroy.isPending ? <><Loader2 size={13} className="animate-spin mr-1.5" />Destroying…</> : 'Destroy permanently'}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Type <code className="font-mono text-fg">{workspaceId}</code> to confirm <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={confirm}
            onChange={e => setConfirm(e.target.value)}
            autoFocus
            placeholder={workspaceId}
            disabled={destroy.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-[color:var(--danger)] focus:outline-none text-sm font-mono"
          />
          {confirm.length > 0 && confirm !== workspaceId && (
            <div className="mt-1 text-2xs text-[color:var(--danger)] inline-flex items-center gap-1">
              <AlertCircle size={11} /> Doesn't match the instance ID
            </div>
          )}
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Reason <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. customer churned, replaced by new clone"
            disabled={destroy.isPending}
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          />
          <div className="mt-1 text-2xs text-fg-subtle">Audit logged. Required for sev/compliance reviews later.</div>
        </div>
      </div>
    </Modal>
  );
}

function RenameWorkspaceModal({
  open,
  onClose,
  tenantSlug,
  workspaceId,
  currentName,
}: {
  open: boolean;
  onClose: () => void;
  tenantSlug: string;
  workspaceId: string;
  currentName: string;
}) {
  const [name, setName] = useState(currentName);
  const patch = usePatchWorkspace();

  // Sync local state when the prop changes (e.g., navigating between workspaces)
  if (open && name !== currentName && patch.isIdle) {
    // no-op — let user edit
  }

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed || trimmed === currentName) { onClose(); return; }
    try {
      await patch.mutateAsync({ tenantId: tenantSlug, workspaceId, name: trimmed });
      toast.success('Renamed', `Workspace is now "${trimmed}".`);
      onClose();
    } catch (e: any) {
      toast.danger('Rename failed', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { setName(currentName); onClose(); }}
      size="sm"
      title="Rename instance"
      description="Changes the display name only. The workspace ID and hostname stay the same."
      footer={
        <>
          <Button variant="ghost" onClick={() => { setName(currentName); onClose(); }} disabled={patch.isPending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={!name.trim() || name.trim() === currentName || patch.isPending}>
            {patch.isPending ? 'Saving…' : 'Rename'}
          </Button>
        </>
      }
    >
      <div>
        <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
          Display name
        </label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          autoFocus
          className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
        />
      </div>
    </Modal>
  );
}

function ResizeWorkspaceModal({
  open,
  onClose,
  tenantSlug,
  workspaceId,
  workspaceName,
  currentSize,
}: {
  open: boolean;
  onClose: () => void;
  tenantSlug: string;
  workspaceId: string;
  workspaceName: string;
  currentSize: DropletSize;
}) {
  const [size, setSize] = useState<DropletSize>(currentSize);
  const [reason, setReason] = useState('');
  const resize = useResizeWorkspace();

  async function submit() {
    if (size === currentSize) { onClose(); return; }
    if (!reason.trim()) return;
    try {
      const r = await resize.mutateAsync({
        tenantId: tenantSlug,
        workspaceId,
        droplet_size: size,
        reason: reason.trim(),
      });
      toast.success(`${workspaceName} resized`, r.message);
      onClose();
      setReason('');
    } catch (e: any) {
      toast.danger('Resize failed', e?.message ?? 'Unknown error');
    }
  }

  const sizes: DropletSize[] = ['s-1vcpu-2gb', 's-2vcpu-4gb', 's-4vcpu-8gb', 's-8vcpu-16gb', 's-16vcpu-32gb'];

  return (
    <Modal
      open={open}
      onClose={() => { setSize(currentSize); setReason(''); onClose(); }}
      size="md"
      title={`Resize ${workspaceName}`}
      description="Powers the droplet off, resizes, then powers it back on. ~2-3 min of downtime."
      footer={
        <>
          <Button variant="ghost" onClick={() => { setSize(currentSize); setReason(''); onClose(); }} disabled={resize.isPending}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={size === currentSize || !reason.trim() || resize.isPending}>
            {resize.isPending ? <><Loader2 size={13} className="animate-spin mr-1.5" />Resizing…</> : `Resize to ${size}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3.5">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            New droplet size
          </label>
          <div className="rounded-md border border-border divide-y divide-border">
            {sizes.map(s => {
              const selected = size === s;
              const isCurrent = currentSize === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSize(s)}
                  className={cn(
                    'w-full px-3 py-2 flex items-center justify-between gap-3 text-left transition-colors',
                    selected ? 'bg-[color:var(--accent-soft)]' : 'hover:bg-surface-2',
                  )}
                >
                  <div>
                    <div className="text-sm text-fg font-medium">{DROPLET_SIZE_LABEL[s]}</div>
                    <div className="text-2xs text-fg-subtle font-mono">{s}</div>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="tabnum text-fg-muted">${DROPLET_SIZE_COST[s]}/mo</span>
                    {isCurrent && <Badge tone="accent">Current</Badge>}
                    {selected && !isCurrent && <CheckCircle2 size={14} className="text-accent" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Reason <span className="text-[color:var(--danger)]">*</span>
          </label>
          <input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. CPU saturation, customer upgrade request"
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm"
          />
          <div className="mt-1 text-2xs text-fg-subtle">Logged to audit + ticketed for the customer.</div>
        </div>
      </div>
    </Modal>
  );
}

function AddCustomDomainModal({
  open,
  onClose,
  tenantSlug,
  workspaceId,
  defaultHostname,
}: {
  open: boolean;
  onClose: () => void;
  tenantSlug: string;
  workspaceId: string;
  defaultHostname: string | null;
}) {
  const [domain, setDomain] = useState('');
  const addDomain = useAddCustomDomain();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const value = domain.trim().toLowerCase();
    if (!value) return;
    try {
      await addDomain.mutateAsync({ tenantId: tenantSlug, workspaceId, domain: value });
      toast.success(`${value} added`, 'Set up a CNAME at your DNS provider pointing to the default hostname.');
      setDomain('');
      onClose();
    } catch (e: any) {
      toast.danger('Could not add domain', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { setDomain(''); onClose(); }}
      size="md"
      title="Add a custom domain"
      description={
        defaultHostname ? (
          <>The customer should add a <strong>CNAME</strong> record at their DNS provider pointing the chosen hostname to <code className="font-mono text-xs">{defaultHostname}</code>. The workspace will start serving the domain once DNS propagates and a TLS cert is issued.</>
        ) : (
          <>Configure DNS at the customer's provider to point at this workspace's default hostname.</>
        )
      }
      footer={
        <>
          <Button variant="ghost" onClick={() => { setDomain(''); onClose(); }}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submit as any}
            disabled={!domain.trim() || addDomain.isPending}
          >
            {addDomain.isPending ? 'Adding…' : 'Add domain'}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="block text-2xs uppercase tracking-wider text-fg-subtle font-semibold mb-1.5">
            Domain
          </label>
          <input
            value={domain}
            onChange={e => setDomain(e.target.value)}
            placeholder="e.g. data.statsdrone.com"
            autoFocus
            className="w-full h-8 px-2.5 rounded-md bg-surface border border-border focus:border-accent focus:outline-none text-sm font-mono"
          />
          <div className="mt-1 text-2xs text-fg-subtle">
            Bare hostname only (no <code className="font-mono">https://</code>, no path).
          </div>
        </div>

        {defaultHostname && domain.trim() && (
          <div className="rounded-md border border-border bg-surface-2/50 px-3 py-2.5 text-xs">
            <div className="font-semibold text-fg mb-1">DNS record the customer adds:</div>
            <div className="font-mono text-2xs text-fg-muted leading-relaxed">
              Type: CNAME<br />
              Name: {domain.trim()}<br />
              Target: {defaultHostname}<br />
              TTL: auto / 300
            </div>
          </div>
        )}
      </form>
    </Modal>
  );
}


function LinkDropletModal({
  open,
  onClose,
  tenantSlug,
  workspaceId,
  workspaceName,
}: {
  open: boolean;
  onClose: () => void;
  tenantSlug: string;
  workspaceId: string;
  workspaceName: string;
}) {
  const { data: droplets = [], isLoading, error } = useDroplets();
  const linkMutation = useLinkDroplet();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Show unlinked droplets first, then linked ones (greyed out).
  const sorted = [...droplets].sort((a, b) => {
    const aLinked = Boolean(a.linked_workspace_id);
    const bLinked = Boolean(b.linked_workspace_id);
    if (aLinked !== bLinked) return aLinked ? 1 : -1;
    return a.name.localeCompare(b.name);
  });

  async function submit() {
    if (!selectedId) return;
    try {
      const r = await linkMutation.mutateAsync({ tenantId: tenantSlug, workspaceId, dropletId: selectedId });
      toast.success(`Linked to droplet`, r.message);
      setSelectedId(null);
      onClose();
    } catch (e: any) {
      toast.danger('Link failed', e?.message ?? 'Unknown error');
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => { setSelectedId(null); onClose(); }}
      size="lg"
      title={`Link droplet to ${workspaceName}`}
      description={
        <>
          Picks one of the droplets in your DigitalOcean account and writes its ID into{' '}
          <code className="font-mono text-xs">backend/tenants.yaml</code>. Restart, snapshot, and suspend will work once linked.
        </>
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!selectedId || linkMutation.isPending}
          >
            {linkMutation.isPending ? 'Linking…' : 'Link selected droplet'}
          </Button>
        </>
      }
    >
      {error ? (
        <div className="px-4 py-8 text-center">
          <AlertCircle size={20} className="mx-auto text-[color:var(--danger)] mb-2" />
          <div className="text-sm font-semibold text-fg">Couldn't reach DigitalOcean</div>
          <div className="text-xs text-fg-muted mt-1 max-w-md mx-auto">
            Check <code className="font-mono">DO_TOKEN</code> in <code className="font-mono">backend/.env</code>, then restart the backend.
          </div>
        </div>
      ) : isLoading ? (
        <div className="px-4 py-8 text-center text-sm text-fg-muted">Loading droplets…</div>
      ) : droplets.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <div className="text-sm font-semibold text-fg">No droplets found</div>
          <div className="text-xs text-fg-muted mt-1">
            Your DigitalOcean account doesn't have any droplets, or the token doesn't have read access.
          </div>
        </div>
      ) : (
        <ul className="rounded-md border border-border divide-y divide-border max-h-[50vh] overflow-y-auto">
          {sorted.map(d => {
            const isLinked = Boolean(d.linked_workspace_id);
            const isMine = d.linked_workspace_id === workspaceId;
            const disabled = isLinked && !isMine;
            return (
              <li key={d.id}>
                <button
                  onClick={() => !disabled && setSelectedId(d.id)}
                  disabled={disabled}
                  className={cn(
                    'w-full px-3 py-2.5 flex items-center justify-between gap-3 text-left transition-colors',
                    selectedId === d.id && 'bg-[color:var(--accent-soft)]',
                    disabled && 'opacity-50 cursor-not-allowed',
                    !disabled && selectedId !== d.id && 'hover:bg-surface-2',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-fg">{d.name}</span>
                      <Badge tone={d.status === 'active' ? 'success' : 'warn'}>
                        {d.status}
                      </Badge>
                      {isMine && <Badge tone="accent">Current</Badge>}
                      {isLinked && !isMine && (
                        <Badge tone="neutral">Linked to {d.linked_workspace_id}</Badge>
                      )}
                    </div>
                    <div className="text-2xs text-fg-subtle font-mono mt-0.5">
                      {d.id} · {d.region} · {d.size}
                      {d.ipv4 && ` · ${d.ipv4}`}
                    </div>
                  </div>
                  {selectedId === d.id && (
                    <CheckCircle2 size={16} className="text-accent shrink-0" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
